"use client"

import { useState } from "react"
import Image from "next/image"
import { ShoppingCart } from "lucide-react"
import { useForm } from "react-hook-form"

import { DishItem } from "@/lib/types/dishes"
import { getPlaceHolderDishImage } from "@/lib/utils"
import { MenuItemCustomizeItemList } from "@/features/resto-detail/components/menu-item-detail-customize-item"
import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { Textarea } from "@/components/ui/textarea"
import { NumberIncrementer } from "@/components/number-incrementer"

interface DishDetailClientProps {
  dish: DishItem
}

export function DishDetailClient({ dish }: DishDetailClientProps) {
  const [quantity, setQuantity] = useState(1)
  const form = useForm()

  const handleIncrement = () => {
    setQuantity((prev) => prev + 1)
  }

  const handleDecrement = () => {
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1))
  }

  const handleAddToCart = (e: React.FormEvent) => {
    e.preventDefault()
    // Add to cart functionality would go here
    console.log("Adding to cart:", { dish, quantity })
  }

  return (
    <Form {...form}>
      <form onSubmit={handleAddToCart}>
        <div className="flex flex-col">
          {/* Dish Image - Mobile */}
          <Image
            src={
              dish.item_image ||
              dish.item_image_compressed ||
              dish.item_image_compressed_web ||
              "/dish_thumbnail.webp"
            }
            alt={dish.item_name || ""}
            width={500}
            height={300}
            placeholder="blur"
            blurDataURL={getPlaceHolderDishImage()}
            className="w-full aspect-[16/9] object-cover block lg:hidden mb-4"
            quality={98}
          />

          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center sticky top-0 bg-white pb-2 z-10">
              <h1 className="text-2xl font-semibold pr-4">{dish.item_name}</h1>
              <span className="font-semibold text-xl">
                {dish.display_price}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <ShoppingCart size={16} className="text-gray-500" />
                <span className="text-sm text-gray-500">
                  {dish.order_count || 0} đã bán
                </span>
              </div>
            </div>

            <p className="text-gray-700 text-base text-justify leading-relaxed">
              {dish.item_details}
            </p>

            <Textarea
              placeholder="Viết lời nhắn cho nhà hàng"
              className="placeholder:text-gray-500 placeholder:uppercase placeholder:text-xs placeholder:font-semibold focus:outline-none focus-visible:ring-0 mt-2"
              {...form.register("note")}
            />

            <div className="mt-5 flex flex-col gap-6">
              {dish.customize_item &&
                dish.customize_item.map((item) => (
                  <MenuItemCustomizeItemList
                    key={item.customize_id}
                    data={item}
                    register={form.register}
                    control={form.control}
                    hasSubmitted={false}
                    isRestoClosed={false}
                  />
                ))}
            </div>
          </div>

          <div className="flex gap-4 mt-8 w-full border-t border-gray-200 pt-5">
            <NumberIncrementer
              value={quantity}
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
              min={1}
            />

            <Button
              type="submit"
              className="flex-1 h-12 rounded-xl"
              variant="be"
            >
              Thêm vào giỏ hàng
            </Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
